import { Component } from '@theme/component';

/**
 * @typedef {Object} GvsCollectionTabsRefs
 * @property {HTMLElement[]} tabs - The tab buttons
 * @property {HTMLElement[]} panels - The tab panels, in the same order as the tabs
 */

/**
 * Switches between collection tabs.
 *
 * The panels are all server-rendered; only their visibility changes. Product
 * images inside a hidden panel are lazy-loaded, so they cost nothing until
 * their tab is opened.
 *
 * Follows the APG tabs pattern: one tab stop for the whole tablist, arrow keys
 * move between tabs.
 *
 * @extends {Component<GvsCollectionTabsRefs>}
 */
class GvsCollectionTabs extends Component {
  requiredRefs = ['tabs', 'panels'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * Bound via `on:click` on each tab button.
   * @param {Event} event
   */
  selectTab = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[role="tab"]');
    if (!(tab instanceof HTMLElement)) return;

    const index = this.refs.tabs.indexOf(tab);
    if (index !== -1) this.#activate(index);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const { tabs } = this.refs;
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return;

    const current = tabs.indexOf(active);
    if (current === -1) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next);
    tabs[next]?.focus();
  };

  /** @param {number} index */
  #activate(index) {
    const { tabs, panels } = this.refs;

    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      // Roving tabindex: only the selected tab is reachable via Tab.
      tab.tabIndex = selected ? 0 : -1;
    });

    panels.forEach((panel, i) => {
      panel.classList.toggle('hidden', i !== index);
    });
  }
}

if (!customElements.get('gvs-collection-tabs-component')) {
  customElements.define('gvs-collection-tabs-component', GvsCollectionTabs);
}
